import styled from '@emotion/styled';
import { useQuery } from '@tanstack/react-query';
import { V1Service } from '@zelos/codegen/apis';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Regulation from './Regulation';
import Button from '@/components/comm/Button';
import Checkbox from '@/components/comm/Checkbox';
import Divider from '@/components/comm/Divider';
import ImageButton from '@/components/comm/ImageButton';
import Margin from '@/components/comm/Margin';
import Modal from '@/components/comm/Modal';
import { breakpointMedium } from '@/constants/breakpoints';
import { colors } from '@/constants/colors';
import { useIdentity } from '@/lib/identity-service';
import { showToast } from '@/lib/toast';
import icClear from '@/public/images/ic-clear.svg';

type GuardianModalProps = {
  onSuccess?: () => void,
  showState: StateType<boolean>,
};

type ModalCase = 'question' | 'auth' | 'fail';

type SuccessCallbackObject = {
  onSuccess: () => void,
};

const Container = styled.section`
min-width: 600px;
align-self: center;
background-color: ${colors.white};
border-radius: 15px;
@media (max-width: ${breakpointMedium}) {
  min-width: 0;
  max-width: 90vw;
}
`;

const Header = styled.div`
display: flex;
height: 60px;
align-items: center;
justify-content: space-between;
margin: 0 40px;
color: ${colors.textPrimary};
font-size: 18px;
font-weight: 700;
letter-spacing: -0.01em;
line-height: 150%;
`;

const Content = styled.div`
margin: 24px 40px;
color: ${colors.black800};
font-size: 16px;
font-weight: 400;
letter-spacing: -0.01em;
line-height: 170%;
`;

const Bottom = styled.div`
display: flex;
justify-content: flex-end;
margin: 24px 40px;
column-gap: 12px;
`;

const GuardianModal = ({ onSuccess, showState }: GuardianModalProps) => {
  const [show, setShow] = showState;
  const [modalCase, setModalCase] = useState<ModalCase>('question');
  const [agree, setAgree] = useState(false);
  const [failMessage, setFailMessage] = useState('');

  const { data: regulationsList } = useQuery(
    ['v1RegulationsList', { scope: 'GUC' }],
    () => V1Service.v1RegulationsList({ scope: 'GUC' }),
    { refetchOnWindowFocus: false },
  );

  const { startIdentity } = useIdentity({
    onSuccessCallback: () => {
      showToast({ text: '보호자 인증이 완료되었습니다.' });
      onSuccess?.();
      setShow(false);
    },
    onErrorCallback: (message) => {
      setModalCase('fail');
      setFailMessage(message);
    },
  });

  const title = useMemo(() => {
    switch (modalCase) {
      case 'question':
        return '만 14세 미만인가요?';
      case 'auth':
        return '보호자 본인인증';
      case 'fail':
        return '보호자 본인인증 실패';
      default: return '';
    }
  }, [modalCase]);

  const Contents = useCallback(() => {
    switch (modalCase) {
      case 'question':
        return (
          <Content>
            <p>만 14세 미만은 보호자 동의가 필요합니다.</p>
          </Content>
        );
      case 'auth':
        return (
          <Content>
            <p>계속 진행하려면 보호자 본인 인증이 필요합니다.</p>
            <Margin vertical="8px" />
            {regulationsList?.[0] && (
              <Checkbox
                boxStyle={{
                  color: colors.grey4,
                  fontSize: 13,
                  fontWeight: 400,
                  lineHeight: 19,
                }}
                checked={agree}
                onChange={() => {
                  setAgree(!agree);
                }}
              >
                <Regulation regulation={regulationsList[0]} />
              </Checkbox>
            )}
          </Content>
        );
      case 'fail':
        return (
          <Content>
            <p>{failMessage}</p>
          </Content>
        );
      default: return null;
    }
  }, [agree, failMessage, regulationsList, modalCase]);

  const StepButtons = useCallback(() => {
    switch (modalCase) {
      case 'question':
        return (
          <Bottom>
            <Button
              padding="12px 20px"
              styleType="grey"
              onClick={() => {
                setShow(false);
              }}
            >아니오
            </Button>
            <Button
              padding="12px 20px"
              onClick={() => {
                setModalCase('auth');
              }}
            >네
            </Button>
          </Bottom>
        );
      case 'auth':
        return (
          <Bottom>
            <Button
              padding="12px 20px"
              onClick={() => {
                if (!agree) showToast({ text: '개인정보 수집 및 이용에 동의해주세요.' });
                else startIdentity('guardian-general');
              }}
            >동의
            </Button>
          </Bottom>
        );
      case 'fail':
        return (
          <Bottom>
            <Button
              padding="12px 20px"
              onClick={() => {
                setShow(false);
              }}
            >확인
            </Button>
          </Bottom>
        );
      default: return null;
    }
  }, [agree, setShow, startIdentity, modalCase]);

  useEffect(() => {
    show && (setModalCase('question'), setAgree(false));
  }, [show]);

  return (
    <Modal show={[show, setShow]}>
      <Container>
        <Header>
          <span>{title}</span>
          <ImageButton
            src={icClear}
            alt="clear"
            onClick={() => setShow(false)}
          />
        </Header>
        <Divider />
        <Contents />
        <StepButtons />
      </Container>
    </Modal>
  );
};

export const useGuardianModal = () => {
  const [show, setShow] = useState(false);
  const [callbackObject, setCallbackObject] = useState<SuccessCallbackObject>();

  return {
    GuardianModal: useCallback(() =>
      <GuardianModal showState={[show, setShow]} onSuccess={callbackObject?.onSuccess} />,
    [callbackObject, show]),
    showGuardianModal: (onSuccess: () => void) => {
      setShow(true);
      setCallbackObject({ onSuccess });
    },
  };
};
